import React, { useEffect, useState } from "react";
import { Text, Flex, Box, type BoxProps } from "@chakra-ui/react";
import { useAnimate, useInView } from "framer-motion";
import * as Sentry from "@sentry/nextjs";
import { CursorIcon, CheckIcon } from "ui/icons";
import { BrazeIcon, GoogleAdsIcon, IterableIcon } from "ui/icons/integrations";
import { DecorativeBackgroundImage } from "components/images";
import bgImage from "../../../../public/assets/platform/customer-studio/activation-bg.png";
import { FacebookTagIcon } from "./facebook-tag";
import { ChecklistItem } from "./checklist-item";
import { IconTag } from "./icon-tag";
import { Checkbox } from "./checkbox";

const green500 = "#2EA281";
const gray300 = "#BDC7C2";

function Row({ children, ...props }: BoxProps) {
  return (
    <Box pos="relative" px={{ base: 3, sm: 4, xl: "small" }} py={{ base: 2, sm: 3, xl: "smaller" }} w="100%" {...props}>
      {children}
    </Box>
  );
}

function useSyncTemplatesAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? [
                // Animation sequence
                ["#activation-cursor", { opacity: 1 }, { delay: 0.7, duration: 0.25 }],
                ["#check-icon-1", { opacity: 1, scale: 1 }, { at: "+.2", duration: 0.25 }],
                ["#checkbox-1", { borderColor: green500, backgroundColor: "#F4FAFA" }, { at: "-0.25", duration: 0.25 }],
                ["#activation-cursor", { y: [0, 40] }, { at: "-0.25", type: "spring", stiffness: 1000, damping: 50 }],
                ["#check-icon-2", { opacity: 1, scale: 1 }, { at: "+.5", duration: 0.25 }],
                ["#checkbox-2", { borderColor: green500, backgroundColor: "#F4FAFA" }, { at: "-0.25", duration: 0.25 }],
                ["#activation-cursor", { y: [44, 80] }, { at: "-0.25", type: "spring", stiffness: 1000, damping: 50 }],
                ["#check-icon-3", { opacity: 1, scale: 1 }, { at: "+.5", duration: 0.25 }],
                ["#checkbox-3", { borderColor: green500, backgroundColor: "#F4FAFA" }, { at: "-0.25", duration: 0.25 }],
                ["#activation-cursor", { opacity: 0 }, { at: "+.3", duration: 0.25 }],
              ]
            : [
                // Reset
                ["#activation-cursor", { y: 0 }, { duration: 0.05 }],
                ["#check-icon-1", { opacity: 0, scale: 0.9 }, { duration: 0.05 }],
                ["#check-icon-2", { opacity: 0, scale: 0.9 }, { duration: 0.05 }],
                ["#check-icon-3", { opacity: 0, scale: 0.9 }, { duration: 0.05 }],
                ["#checkbox-1", { borderColor: gray300, backgroundColor: "white" }, { duration: 0.05 }],
                ["#checkbox-2", { borderColor: gray300, backgroundColor: "white" }, { duration: 0.05 }],
                ["#checkbox-3", { borderColor: gray300, backgroundColor: "white" }, { duration: 0.05 }],
              ],
        );
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

export function SyncTemplatesAnimation() {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useSyncTemplatesAnimation(animationActive);
  const isInView = useInView(scope, { amount: 0.5 });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex
      ref={scope}
      align="center"
      bg="new.blue.150"
      boxSize="100%"
      justify="center"
      pos="relative"
      p={{ base: "smaller", xl: "bigger" }}
    >
      <DecorativeBackgroundImage src={bgImage} />
      <Flex
        align="start"
        bg="white"
        borderRadius="small"
        boxShadow="lg"
        flexDir="column"
        justify="start"
        pt={{ base: 0, lg: "tiniest" }}
        pb={{ base: 0, lg: "tiny" }}
        pos="relative"
        w="100%"
      >
        <Row>
          <Text textStyle="headline3" fontSize={{ base: "brand.md", md: "brand.sm", lg: "brand.lg" }}>
            Add syncs
          </Text>
        </Row>

        <Row borderY="1px" borderColor="brand.border.extraLight" bg="brand.backgroundSecondary.light">
          <Text textStyle={{ base: "caption2", lg: "caption1" }}>
            Sync templates are pre-configured to work with this Audience&apos;s data source.
          </Text>

          <Flex
            align="start"
            direction="column"
            gap={{ base: "tiny", md: "smallest" }}
            textStyle={{ base: "caption2", lg: "caption1" }}
            mt={{ base: "smallest", md: "smaller" }}
          >
            <Text fontWeight="semibold">Active syncs</Text>
            <IconTag icon={FacebookTagIcon}>Facebook Custom Audiences</IconTag>
          </Flex>
        </Row>

        <Row>
          <Text
            textStyle={{ base: "caption2", lg: "caption1" }}
            fontWeight="semibold"
            mt={{ base: "tiny", lg: 0 }}
            mb={{ base: "tiny", md: "tiniest", lg: "tiny" }}
          >
            Add sync template
          </Text>

          <ChecklistItem>
            <Checkbox id="checkbox-1">
              <CheckIcon id="check-icon-1" boxSize="14px" color="brand.accent.secondary" opacity={0} />
            </Checkbox>
            <BrazeIcon boxSize="smaller" />
            Users to Braze
          </ChecklistItem>

          <ChecklistItem>
            <Checkbox id="checkbox-2">
              <CheckIcon id="check-icon-2" boxSize="14px" color="brand.accent.secondary" opacity={0} />
            </Checkbox>
            <GoogleAdsIcon boxSize="smaller" />
            Users to Google Ads
          </ChecklistItem>

          <ChecklistItem>
            <Checkbox id="checkbox-3">
              <CheckIcon id="check-icon-3" boxSize="14px" color="brand.accent.secondary" opacity={0} />
            </Checkbox>
            <IterableIcon boxSize="smaller" />
            Users to Iterable
          </ChecklistItem>

          <CursorIcon
            id="activation-cursor"
            boxSize="18px"
            pos="absolute"
            left="32px"
            top="60px"
            opacity={0}
            zIndex={1}
          />
        </Row>
      </Flex>
    </Flex>
  );
}
